'use client';

import dynamic from 'next/dynamic';
import type { ReactNode } from 'react';
import { Suspense, useState } from 'react';

import { Button } from '@/app/components/button';
import { Container } from '@/app/components/layouts/container';
import { MainMenuDesktop } from '@/app/components/layouts/top-navigation/main-menu/desktop';
import { PathWatcher } from '@/app/components/layouts/top-navigation/path-watcher';
import { TopNavigationLogo } from '@/app/components/layouts/top-navigation/shared/logo';
import { SubMenu } from '@/app/components/layouts/top-navigation/sub-menu';
import {
  TOP_NAV_DATA_GA,
  TOP_NAV_DATA_TEST,
} from '@/app/components/layouts/top-navigation/tracking';
import {
  TOP_NAV_MENU_LABEL,
  TOP_NAV_Z_CLASSNAME,
} from '@/app/constants/top-navigation';
import { NAV_INTEREST } from '@/app/contents/top-navigation';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
import type {
  NavItemType,
  TopNavigationVariantType,
} from '@/app/types/top-navigation';
import type { VehicleType } from '@/app/types/vehicle-type';
import { cn } from '@/app/utils/cn';
import { cnva } from '@/app/utils/cnva';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import {
  clearBodyLockScroll,
  enableBodyLockScroll,
} from '@/app/utils/lock-scroll';

const MainMenuMobile = dynamic(
  () =>
    import('@/app/components/layouts/top-navigation/main-menu/mobile').then(
      (mod) => mod.MainMenuMobile
    ),
  {
    ssr: false,
  }
);

const TopNavigationInnerStyled = cnva(
  [
    'relative flex h-full items-center justify-between gap-x-4',
    TOP_NAV_Z_CLASSNAME,
  ],
  {
    variants: {
      variant: {
        filled: 'text-gray-700',
        transparent: 'text-white',
      },
    },
    defaultVariants: {
      variant: 'filled',
    },
  }
);

interface TopNavigationProps {
  logo: ReactNode;
  items: NavItemType[];
  vehicleType: VehicleType;
  variant?: TopNavigationVariantType;
}

export const TopNavigation = ({
  logo,
  items,
  vehicleType,
  variant,
}: TopNavigationProps) => {
  const isDesktop = useMediaQuery('(min-width: 1280px)');
  const [shouldRenderMenu, setShouldRenderMenu] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [activeItem, setActiveItem] = useState<NavItemType | undefined>(
    undefined
  );

  const isDesktopMenuOpen = !!isDesktop && isOpen;
  const adjustedVariant = isDesktopMenuOpen ? 'filled' : variant;

  const openMenu = () => {
    if (isOpen && !isDesktop) return;

    setShouldRenderMenu(true);
    setIsOpen(true);
    if (!isDesktop) {
      enableBodyLockScroll({
        onlyOverflow: true,
      });
      setActiveItem(undefined);
    }
  };
  const closeMenu = () => {
    setIsOpen(false);
    clearBodyLockScroll();
  };

  const onMouseLeaveMenu = (e) => {
    if (!isDesktopMenuOpen || !e.relatedTarget) return;

    if (!e.relatedTarget.tagName) {
      closeMenu();
      return;
    }

    if (e.relatedTarget.tagName.toLowerCase() !== 'aside') {
      closeMenu();
    }
  };

  return (
    <>
      <Suspense>
        <PathWatcher
          isOpen={isOpen}
          isDesktop={isDesktop}
          setShouldRenderMenu={setShouldRenderMenu}
          closeMenu={closeMenu}
          setActiveItem={setActiveItem}
        />
      </Suspense>

      <Container
        aria-label="Navigation"
        as="nav"
        className={TopNavigationInnerStyled({
          variant: adjustedVariant,
        })}
        {...createDataAttributes({
          test: TOP_NAV_DATA_TEST.navigation,
        })}
      >
        {!!logo && (
          <TopNavigationLogo
            logo={logo}
            variant={adjustedVariant}
            onMouseEnter={closeMenu}
          />
        )}
        <div
          role="menubar"
          className={cn(
            'flex h-full items-center justify-end gap-x-4 xl:grow',
            {
              'text-gray-700': adjustedVariant === 'filled',
              'text-white': adjustedVariant === 'transparent',
            }
          )}
          onMouseLeave={onMouseLeaveMenu}
        >
          <MainMenuDesktop
            items={items}
            activeItem={activeItem}
            isOpen={isDesktopMenuOpen}
            onOpen={openMenu}
            onSetActiveItem={setActiveItem}
            onClose={closeMenu}
          />
          <Button
            href={NAV_INTEREST.href}
            aria-label={NAV_INTEREST.label}
            size="sm"
            width="auto"
            {...createDataAttributes({
              test: TOP_NAV_DATA_TEST.interestButton,
            })}
            {...createDataGA({
              click: true,
              eventName: TOP_NAV_DATA_GA.topNavigation,
              text: NAV_INTEREST.label,
              url: NAV_INTEREST.href,
            })}
            variant={
              adjustedVariant === 'transparent' ? 'secondary' : 'primary'
            }
            color={adjustedVariant === 'transparent' ? 'white' : 'red'}
            className="whitespace-nowrap"
            prefetch={null}
            onFocus={closeMenu}
            onMouseEnter={closeMenu}
          >
            {NAV_INTEREST.label}
          </Button>
          <Button
            size="sm"
            width="auto"
            variant="secondary"
            color={adjustedVariant === 'filled' ? 'black' : 'white'}
            onClick={openMenu}
            {...createDataAttributes({
              test: TOP_NAV_DATA_TEST.menuButton,
            })}
            {...createDataGA({
              click: true,
              eventName: TOP_NAV_DATA_GA.topNavigation,
              text: TOP_NAV_MENU_LABEL,
            })}
            className="xl:hidden"
          >
            {TOP_NAV_MENU_LABEL}
          </Button>
        </div>
      </Container>
      {!!shouldRenderMenu &&
        (isDesktop ? (
          <SubMenu
            isOpen={isDesktopMenuOpen}
            activeItem={activeItem}
            isDesktop
            onClose={closeMenu}
          />
        ) : (
          <MainMenuMobile
            logo={logo}
            isOpen={isOpen}
            activeItem={activeItem}
            items={items}
            vehicleType={vehicleType}
            onSetActiveItem={setActiveItem}
            onClose={closeMenu}
          />
        ))}
    </>
  );
};
